import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import { debounce, useTheme } from '@sravni/react-utils';
import type { Tab } from '@sravni/ugc/lib/specialOffers/types';

import { normalizeTabsWithOldKursy, normalizeTabWithOldKursy } from '../helpers/normalizeTabWithOldKursy';
import type { MessageParams } from '../helpers/sendMessageToParentWindow';

import { useHasOffersParams } from './hasOffersParams';

export interface IForComponent {
  isMobile?: boolean;
  activeTabType?: Tab;
  maxDesktopVisibleItems?: number;
  allTabs?: Tab[];
  isWithLinkToAll?: boolean;
}

export interface ISettingCommunicationBetweenWindows {
  componentRef: RefObject<HTMLDivElement>;
  abName?: string;
  sendMessageToParentWindow: (params: MessageParams) => void;

  onParamsChange?: (target: PARAMS_FROM_WRAPPER, value) => void;
}

interface IParamsForWrapper {
  pageUrl?: string;
  isMobile?: boolean;
  isWithLinkToAll: boolean;
  activeTabType?: Tab;
  maxDesktopVisibleItems?: number;
  allTabs?: Tab[];
}

export enum PARAMS_FROM_WRAPPER {
  pageUrl = 'pageUrl',
  isMobile = 'isMobile',
  isWithLinkToAll = 'isWithLinkToAll',
  activeTabType = 'activeTabType',
  maxDesktopVisibleItems = 'maxDesktopVisibleItems',
  allTabs = 'allTabs',
  theme = 'theme',
}

export const useSettingCommunicationBetweenWindows = ({
  componentRef,
  abName,
  sendMessageToParentWindow,
  onParamsChange,
}: ISettingCommunicationBetweenWindows): IForComponent => {
  const [_, setTheme] = useTheme();
  const firstRender = useRef<boolean>(true);

  const [paramsFromWrapper, setParamsFromWrapper] = useState<IParamsForWrapper>({
    isWithLinkToAll: true,
  });

  const handleParamsChange = (target: PARAMS_FROM_WRAPPER, newValue, oldParams?: IParamsForWrapper) => {
    setParamsFromWrapper((prev) => ({
      ...prev,
      [target]: newValue,
    }));

    if (!onParamsChange) return;

    if (target === PARAMS_FROM_WRAPPER.activeTabType) {
      onParamsChange(target, newValue);
    } else if (target === PARAMS_FROM_WRAPPER.allTabs) {
      if (JSON.stringify(newValue) === JSON.stringify(oldParams.allTabs)) return;
      onParamsChange(target, newValue);
    }
  };

  useHasOffersParams({ pageUrl: paramsFromWrapper.pageUrl, abName });

  useEffect(() => {
    if (!firstRender.current) return;

    const blockHeight = componentRef.current.offsetHeight;
    sendMessageToParentWindow({ type: 'changeHeight', height: blockHeight });

    firstRender.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentRef]);

  useEffect(() => {
    const block = componentRef.current;

    // eslint-disable-next-line init-declarations
    let resizeObserver: ResizeObserver;

    const initResizeObserver = debounce((mutationObserver: MutationObserver) => {
      resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const { height } = entry.contentRect;

          if (height) {
            sendMessageToParentWindow({ type: 'changeHeight', height });
          }
        });
        mutationObserver.disconnect();
      });

      resizeObserver.observe(block);
    }, 300);

    const mutationObserver = new MutationObserver(() => {
      initResizeObserver(mutationObserver);
    });

    mutationObserver.observe(block, { childList: true, subtree: true });

    return () => {
      resizeObserver?.unobserve(block);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentRef]);

  useEffect(() => {
    const listener = ({ data }: MessageEvent) => {
      const { source, payload } = data;

      if (source !== 'special-offers-iframe-wrapper') return;
      const {
        type: typeFromPayload,
        isMobile: isMobileFromPayload,
        theme,
        pageUrl: pageUrlFromPayload,
        activeTabType: activeTabTypeFromPayload,
        maxDesktopVisibleItems: maxDesktopVisibleItemsFromPayload,
        allTabs: allTabsFromPayload,
        isWithLinkToAll: isWithLinkToAllFromPayload,
      } = payload;

      if (typeFromPayload === 'changeTheme') {
        setTheme(theme);
      }

      if (typeFromPayload === 'changeIsMobile') {
        handleParamsChange(PARAMS_FROM_WRAPPER.isMobile, !!isMobileFromPayload);
        return;
      }

      if (typeFromPayload === 'pageUrl') {
        handleParamsChange(PARAMS_FROM_WRAPPER.pageUrl, pageUrlFromPayload);
        return;
      }

      if (typeFromPayload === 'activeTabType') {
        handleParamsChange(PARAMS_FROM_WRAPPER.activeTabType, normalizeTabWithOldKursy(activeTabTypeFromPayload));
        return;
      }

      if (typeFromPayload === 'maxDesktopVisibleItems') {
        handleParamsChange(PARAMS_FROM_WRAPPER.maxDesktopVisibleItems, maxDesktopVisibleItemsFromPayload);
        return;
      }

      if (typeFromPayload === 'isWithLinkToAll') {
        handleParamsChange(PARAMS_FROM_WRAPPER.isWithLinkToAll, !!isWithLinkToAllFromPayload);
        return;
      }

      if (typeFromPayload === 'allTabs') {
        handleParamsChange(
          PARAMS_FROM_WRAPPER.allTabs,
          normalizeTabsWithOldKursy(allTabsFromPayload),
          paramsFromWrapper,
        );
      }
    };

    window.addEventListener('message', listener);

    sendMessageToParentWindow({ type: 'listenerIsAdded' });

    return () => {
      window.removeEventListener('message', listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return paramsFromWrapper;
};
